import React from 'react';

// Common props that all steps will have
export interface BaseStepProps {
  userType: 'startup' | 'investor';
  onCreateProfile?: (formData: any) => void;
  hasProfile?: boolean;
  onComplete: () => void;
  children?: React.ReactNode; // Optional - steps can render their own content
  shouldValidate?: boolean;
  onValidationHandled?: () => void;
  onValidationResult?: (passed: boolean, latestFormData?: any) => void;
}

// Props for form fields
export interface FormFieldProps {
  name: string;
  label: string;
  type?: 'text' | 'email' | 'url' | 'number' | 'tel';
  required?: boolean;
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
}

// Base component that all steps will extend
export const BaseStep: React.FC<BaseStepProps> = ({ children }) => {
  if (children) {
    return (
      <div className="space-y-3 sm:space-y-5">
        <div className="space-y-3 sm:space-y-5">{children}</div>
      </div>
    );
  }

  // If no children, just return empty (for steps that don't use BaseStep wrapper)
  return <div className="space-y-3 sm:space-y-5" />;
};

// Reusable form field component
export const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  type = 'text',
  required = false,
  placeholder,
  value,
  onChange,
  error,
}) => {
  return (
    <div>
      {label ? (
        <label
          htmlFor={name}
          className="block text-sm sm:text-base font-medium text-white mb-1.5 sm:mb-2 text-left"
        >
          {label}
        </label>
      ) : null}
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className={`w-full p-2 sm:p-2.5 text-sm border rounded sm:rounded-md focus:outline-none sm:focus:ring-2 sm:focus:ring-emerald-500 bg-white text-gray-900 ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
        placeholder={placeholder}
      />
      {error && <p className="text-red-500 text-xs sm:text-sm mt-1 text-right">{error}</p>}
    </div>
  );
};

// Hook for managing form data
export type FormDataValue = string | string[] | number[] | number | boolean | [number, number];

export const useFormData = (initialData: Record<string, FormDataValue>) => {
  const [formData, setFormData] = React.useState(initialData);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [hasValidated, setHasValidated] = React.useState(false);

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
      // Clear error when user starts typing
      if (errors[name]) {
        setErrors(prev => {
          if (prev[name]) {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
          }
          return prev;
        });
      }
    },
    [errors]
  );

  const validateForm = React.useCallback(
    (requiredFields: string[]): boolean => {
      setHasValidated(true);
      const newErrors: Record<string, string> = {};
      let isValid = true;

      requiredFields.forEach(field => {
        const value = formData[field];
        if (
          !value ||
          (typeof value === 'string' && value.trim() === '') ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'number' && (isNaN(value) || value < 0)) ||
          (Array.isArray(value) &&
            value.length === 2 &&
            typeof value[0] === 'number' &&
            typeof value[1] === 'number' &&
            (isNaN(value[0]) || isNaN(value[1]) || value[1] <= value[0]))
        ) {
          newErrors[field] = 'This field is required';
          isValid = false;
        }
      });

      setErrors(newErrors);
      return isValid;
    },
    [formData]
  );

  const clearFieldError = React.useCallback((field: string) => {
    setErrors(prev => {
      if (!prev[field]) return prev;
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const resetForm = React.useCallback(() => {
    setFormData(initialData);
    setErrors({});
    setHasValidated(false);
  }, [initialData]);

  return {
    formData,
    errors,
    hasValidated,
    handleInputChange,
    validateForm,
    clearFieldError,
    resetForm,
    setFormData,
    setErrors,
  };
};

export default BaseStep;
